/**
 * Servicio de versiones
 * Sprint 6: Consulta versión mínima del backend y compara con la local
 */

import { apiClient } from './api';
import { Capacitor } from '@capacitor/core';
import type { AppPlatform, VersionCheckResult } from '../types/version';

const VERSION_CHECK_TIMEOUT_MS = 8000;

async function withTimeout<T>(promise: Promise<T>, timeoutMs: number, timeoutMessage: string): Promise<T> {
    return await Promise.race([
        promise,
        new Promise<T>((_, reject) => {
            setTimeout(() => {
                reject(new Error(timeoutMessage));
            }, timeoutMs);
        }),
    ]);
}

/**
 * Respuesta cruda del endpoint (apiClient ya parsea el JSON)
 */
interface VersionApiResponse {
    data: {
        platform: AppPlatform;
        minimum_version: string;
        current_version: string;
        download_url: string | null;
    };
}

/**
 * Detectar plataforma actual (Capacitor)
 */
function getPlatform(): AppPlatform {
    const platform = Capacitor.getPlatform();

    if (platform === 'android') return 'android';
    if (platform === 'ios') return 'ios';
    return 'web';
}

/**
 * Comparar dos versiones semánticas (major.minor.patch)
 * @returns negativo si v1 < v2, 0 si iguales, positivo si v1 > v2
 */
function compareVersions(v1: string, v2: string): number {
    const [major1 = 0, minor1 = 0, patch1 = 0] = v1.split('.').map(Number);
    const [major2 = 0, minor2 = 0, patch2 = 0] = v2.split('.').map(Number);

    if (major1 !== major2) return major1 - major2;
    if (minor1 !== minor2) return minor1 - minor2;
    return patch1 - patch2;
}

export const versionService = {
    /**
     * Consultar versión mínima requerida y verificar contra la versión local
     */
    checkVersion: async (localVersion: string): Promise<VersionCheckResult> => {
        const platform = getPlatform();

        const response = await withTimeout(
            apiClient.get<VersionApiResponse>(
                `/api/system/version?platform=${platform}`,
                false // endpoint público, sin auth
            ),
            VERSION_CHECK_TIMEOUT_MS,
            'Tiempo de espera agotado al verificar versión.'
        );

        const { minimum_version, current_version, download_url } = response.data;

        const updateRequired = compareVersions(localVersion, minimum_version) < 0;

        return {
            updateRequired,
            minimumVersion: minimum_version,
            currentVersion: current_version,
            downloadUrl: download_url,
            localVersion,
        };
    },

    /**
     * Exponer compareVersions para testing / uso externo
     */
    compareVersions,
};
